import React, { useEffect, useMemo, useState } from "react";
import { appConfirm } from "./AppDialog";
import {
  deleteAdminAccountDeletionSurvey,
  getAdminAccountDeletionSurveys,
} from "../services/adminDashboardService";
import "../styles/pages/adminDashboard.css";

const REASONS = [
  "setup_unclear", "expected_something_else", "no_family_invite", "privacy_concern",
  "too_many_steps", "technical_problem", "missing_feature", "just_testing",
  "not_ready", "other", "prefer_not_to_say",
];
const PIE_COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ec4899"];

const duration = (seconds, tr) => {
  const value = Math.max(0, Number(seconds || 0));
  if (value < 60) return `${value} ${tr ? "sn" : "sec"}`;
  if (value < 3600) return `${Math.floor(value / 60)} ${tr ? "dk" : "min"} ${value % 60} ${tr ? "sn" : "sec"}`;
  return `${Math.floor(value / 3600)} ${tr ? "sa" : "hr"} ${Math.floor((value % 3600) / 60)} ${tr ? "dk" : "min"}`;
};

export default function AccountDeletionSurveyAdminPanel({ tr = true, managementContext = false }) {
  const [filters, setFilters] = useState({ days: "30", search: "", reason: "", platform: "", language: "", appVersion: "" });
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, reasons: [], platforms: [], languages: [], appVersions: [], daily: [] });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState("");
  const [error, setError] = useState("");

  const reasonLabel = (key) => ({
    setup_unclear: tr ? "Aile kurulumunu anlayamadı" : "Setup was unclear",
    expected_something_else: tr ? "Farklı bir uygulama bekliyordu" : "Expected something else",
    no_family_invite: tr ? "Aile davet kodu yok" : "No family invite",
    privacy_concern: tr ? "Gizlilik endişesi" : "Privacy concern",
    too_many_steps: tr ? "Çok fazla adım" : "Too many steps",
    technical_problem: tr ? "Teknik sorun" : "Technical problem",
    missing_feature: tr ? "Eksik özellik" : "Missing feature",
    just_testing: tr ? "Yalnızca deniyordu" : "Just testing",
    not_ready: tr ? "Kullanmaya hazır değil" : "Not ready",
    other: tr ? "Diğer" : "Other",
    prefer_not_to_say: tr ? "Belirtmek istemedi" : "Preferred not to say",
  }[key] || key);

  const load = async (page = 1, nextFilters = filters) => {
    try {
      setLoading(true);
      setError("");
      const data = await getAdminAccountDeletionSurveys(
        { ...nextFilters, page, limit: 20 },
        { managementContext }
      );
      setItems(data.surveys || []);
      setSummary(data.summary || {});
      setPagination(data.pagination || { page, pages: 1, total: 0 });
    } catch (loadError) {
      setError(loadError?.message || (tr ? "Anket kayıtları alınamadı." : "Survey records could not be loaded."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pieStyle = useMemo(() => {
    const rows = summary.platforms || [];
    const total = rows.reduce((sum, row) => sum + Number(row.count || 0), 0);
    if (!total) return { background: "var(--app-control-bg)" };
    let cursor = 0;
    const stops = rows.slice(0, PIE_COLORS.length).map((row, index) => {
      const start = cursor;
      cursor += (Number(row.count || 0) / total) * 100;
      return `${PIE_COLORS[index]} ${start}% ${cursor}%`;
    });
    return { background: `conic-gradient(${stops.join(",")})` };
  }, [summary.platforms]);

  const maxReason = Math.max(1, ...(summary.reasons || []).map((row) => Number(row.count || 0)));
  const maxDaily = Math.max(1, ...(summary.daily || []).map((row) => Number(row.count || 0)));
  const formatDate = (value) => value
    ? new Date(value).toLocaleString(tr ? "tr-TR" : "en-US", { dateStyle: "medium", timeStyle: "short" })
    : "—";

  const remove = async (item) => {
    const confirmed = await appConfirm(
      tr ? `${item.full_name || "Bu kullanıcı"} için tutulan anket kaydı silinsin mi?` : `Delete the survey record for ${item.full_name || "this user"}?`,
      { title: tr ? "Anket Kaydını Sil" : "Delete Survey Record", confirmText: tr ? "Sil" : "Delete", cancelText: tr ? "İptal" : "Cancel", danger: true }
    );
    if (!confirmed) return;
    try {
      setDeletingId(item.id);
      await deleteAdminAccountDeletionSurvey(item.id, { managementContext });
      const nextPage = items.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
      await load(nextPage, filters);
    } catch (deleteError) {
      setError(deleteError?.message || (tr ? "Anket kaydı silinemedi." : "Survey record could not be deleted."));
    } finally {
      setDeletingId("");
    }
  };

  return (
    <section className="adminControlPanel deletionSurveyAdminPanel">
      <div className="adminControlPanelHeader">
        <div className="adminControlPanelTitle">
          <div className="adminControlPanelIcon">◫</div>
          <div>
            <h3>{tr ? "Hesap Silme Anketi" : "Account Deletion Survey"}</h3>
            <p>{tr ? "Aile kurulumunda hesabını silen kullanıcıların geri bildirimleri" : "Feedback from users who delete their account during family setup"}</p>
          </div>
        </div>
      </div>

      <div className="deletionSurveyStats">
        <div><small>{tr ? "Toplam kayıt" : "Total records"}</small><strong>{summary.total || 0}</strong></div>
        <div><small>{tr ? "Ort. hesap süresi" : "Avg. account age"}</small><strong>{duration(summary.avgAccountAgeSeconds, tr)}</strong></div>
        <div><small>{tr ? "Ort. kurulum ekranı" : "Avg. setup screen"}</small><strong>{duration(summary.avgOnboardingElapsedSeconds, tr)}</strong></div>
      </div>

      <div className="deletionSurveyCharts">
        <div className="deletionSurveyChartCard">
          <h4>{tr ? "Silme nedenleri" : "Deletion reasons"}</h4>
          <div className="deletionSurveyBars">
            {(summary.reasons || []).length ? summary.reasons.map((row) => (
              <div className="deletionSurveyBarRow" key={row.reason}>
                <div><span>{reasonLabel(row.reason)}</span><b>{row.count}</b></div>
                <i><em style={{ width: `${Math.max(4, (Number(row.count || 0) / maxReason) * 100)}%` }} /></i>
              </div>
            )) : <p className="deletionSurveyEmpty">{tr ? "Henüz veri yok" : "No data yet"}</p>}
          </div>
        </div>
        <div className="deletionSurveyChartCard">
          <h4>{tr ? "Platform dağılımı" : "Platform distribution"}</h4>
          <div className="deletionSurveyPieWrap">
            <div className="deletionSurveyPie" style={pieStyle}><span>{summary.total || 0}</span></div>
            <div className="deletionSurveyLegend">
              {(summary.platforms || []).map((row, index) => (
                <span key={row.key}><i style={{ background: PIE_COLORS[index % PIE_COLORS.length] }} />{row.key || "unknown"}<b>{row.count}</b></span>
              ))}
            </div>
          </div>
          <div className="deletionSurveyDaily" aria-label={tr ? "Günlük eğilim" : "Daily trend"}>
            {(summary.daily || []).map((row) => <i key={row.day} title={`${row.day}: ${row.count}`} style={{ height: `${Math.max(8, (Number(row.count || 0) / maxDaily) * 100)}%` }} />)}
          </div>
        </div>
      </div>

      <form className="deletionSurveyFilters" onSubmit={(event) => { event.preventDefault(); load(1, filters); }}>
        <input value={filters.search} onChange={(event) => setFilters((value) => ({ ...value, search: event.target.value }))} placeholder={tr ? "Ad soyad ara" : "Search name"} />
        <select value={filters.days} onChange={(event) => setFilters((value) => ({ ...value, days: event.target.value }))}>
          <option value="7">{tr ? "Son 7 gün" : "Last 7 days"}</option><option value="30">{tr ? "Son 30 gün" : "Last 30 days"}</option><option value="90">{tr ? "Son 90 gün" : "Last 90 days"}</option><option value="3650">{tr ? "Tüm kayıtlar" : "All records"}</option>
        </select>
        <select value={filters.reason} onChange={(event) => setFilters((value) => ({ ...value, reason: event.target.value }))}>
          <option value="">{tr ? "Tüm nedenler" : "All reasons"}</option>{REASONS.map((reason) => <option key={reason} value={reason}>{reasonLabel(reason)}</option>)}
        </select>
        <select value={filters.platform} onChange={(event) => setFilters((value) => ({ ...value, platform: event.target.value }))}><option value="">{tr ? "Tüm platformlar" : "All platforms"}</option><option value="ios">iOS</option><option value="android">Android</option><option value="web">Web</option></select>
        <select value={filters.language} onChange={(event) => setFilters((value) => ({ ...value, language: event.target.value }))}><option value="">{tr ? "Tüm diller" : "All languages"}</option><option value="tr">TR</option><option value="en">EN</option></select>
        <input value={filters.appVersion} onChange={(event) => setFilters((value) => ({ ...value, appVersion: event.target.value }))} placeholder={tr ? "Uygulama sürümü" : "App version"} />
        <button className="adminPrimaryBtn" type="submit" disabled={loading}>{tr ? "Uygula" : "Apply"}</button>
      </form>

      {error && <div className="adminErrorBox">{error}</div>}
      {loading ? <div className="deletionSurveyEmpty">{tr ? "Kayıtlar yükleniyor..." : "Loading records..."}</div> : !items.length ? <div className="deletionSurveyEmpty">{tr ? "Bu filtrelerle eşleşen kayıt yok." : "No records match these filters."}</div> : (
        <div className="deletionSurveyList">
          {items.map((item) => (
            <article className="deletionSurveyItem" key={item.id}>
              <div className="deletionSurveyItemHeader"><div><strong>{item.full_name || "—"}</strong><small>{formatDate(item.created_at)}</small></div><button type="button" className="adminDangerBtn" disabled={deletingId === item.id} onClick={() => remove(item)}>{deletingId === item.id ? "…" : (tr ? "Sil" : "Delete")}</button></div>
              <div className="deletionSurveyReasonChips">{(item.reasons || []).map((reason) => <span key={reason}>{reasonLabel(reason)}</span>)}</div>
              {item.other_text && <p className="deletionSurveyOtherText">“{item.other_text}”</p>}
              <dl className="deletionSurveyMeta">
                <div><dt>{tr ? "Kayıt oldu" : "Registered"}</dt><dd>{formatDate(item.registered_at)}</dd></div>
                <div><dt>{tr ? "Hesapta kaldı" : "Account age"}</dt><dd>{duration(item.account_age_seconds, tr)}</dd></div>
                <div><dt>{tr ? "Kurulum ekranı" : "Setup screen"}</dt><dd>{duration(item.onboarding_elapsed_seconds, tr)}</dd></div>
                <div><dt>{tr ? "Kayıt yöntemi" : "Registration"}</dt><dd>{item.registration_method || "—"}</dd></div>
                <div><dt>{tr ? "Cihaz" : "Device"}</dt><dd>{[item.platform, item.device_model].filter(Boolean).join(" · ") || "—"}</dd></div>
                <div><dt>{tr ? "Sürüm" : "Version"}</dt><dd>{item.app_version ? `v${item.app_version}${item.build_number ? ` (${item.build_number})` : ""}` : (item.build_number ? `build ${item.build_number}` : "—")}</dd></div>
                <div><dt>{tr ? "İşletim sistemi" : "Operating system"}</dt><dd>{item.os_version || "—"}</dd></div>
                <div><dt>{tr ? "Dil" : "Language"}</dt><dd>{String(item.language || "—").toUpperCase()}</dd></div>
              </dl>
            </article>
          ))}
        </div>
      )}

      <div className="deletionSurveyPagination">
        <button className="adminGhostBtn" type="button" disabled={loading || pagination.page <= 1} onClick={() => load(pagination.page - 1, filters)}>{tr ? "Önceki" : "Previous"}</button>
        <span>{pagination.page || 1} / {pagination.pages || 1} · {pagination.total || 0}</span>
        <button className="adminGhostBtn" type="button" disabled={loading || pagination.page >= pagination.pages} onClick={() => load(pagination.page + 1, filters)}>{tr ? "Sonraki" : "Next"}</button>
      </div>
    </section>
  );
}
